import { useState, useEffect } from 'react';
import { Stack, Typography, Divider, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import TimeRange from 'react-video-timelines-slider';
import { format } from 'date-fns';
import { DatePicker as AntdDatePicker } from 'antd';
import getTimelineGaps from '../../utils/getTimelineGaps';
import axiosInstance from '../../utils/axiosInstance';
import SingleSelector from '../single-selector/singleSelector';

const { RangePicker } = AntdDatePicker;

export default function DownloadVideos() {
  const { enqueueSnackbar } = useSnackbar();
  const [camera, setCamera] = useState(null);
  const [timelines, setTimelines] = useState(null);
  const [calendarStartDate, setCalendarStartDate] = useState(null);
  const [calendarEndDate, setCalendarEndDate] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState([]);
  const [disabledIntervals, setDisabledIntervals] = useState([]);
  const [timelineScrubberError, setTimelineScrubberError] = useState(false);
  const [cameraList, setCameraList] = useState([]);
  useEffect(() => {
    fetchCameraList();
  }, []);

  const fetchCameraList = async () => {
    try {
      const { data } = await axiosInstance.get(`${window.location.protocol}//${window.location.host}/api/v1/sensor/list`);
      console.log('unfiltered camera list: ', data);
      for (let i = 0; i < data.length; i += 1) {
        data[i].host = window.location.host;
      }
      setCameraList(data);
    } catch (error) {
      console.log('Failed to fetch device list', error.response);
    }
    return null;
  };

  const downloadVideoDataApi = (jsonData) => {
    let proxy = window.location.pathname;
    let url = `${window.location.protocol}//${camera.host}/api/v1/storage/file/${camera.sensorId}?startTime=${jsonData.start_time}&endTime=${jsonData.end_time}`;
    if (proxy != null && proxy !== '/' && proxy.length > 0) {
      if (proxy[proxy.length - 1] === '/') {
        proxy = proxy.slice(0, -1);
      }
      url = url.replace('/api', `${proxy}/api`);
    }
    const newWindow = window.open(url, '_blank');
    if (!newWindow) {
      // Handle the case where the new window couldn't be opened.
      console.error('Failed to open a new window for the download.');
      // You can also throw an error or pass it to the previous window if needed.
    } else {
      // Check if the download was successful or if an error occurred.
      newWindow.addEventListener('load', () => {
        const errorContent = newWindow.document.documentElement.textContent;
        if (errorContent.includes('error_code') || errorContent.includes('Error')) {
          // Handle the error case based on the content.
          enqueueSnackbar(`Error occurred during the file download.`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
          console.error('download error');
          // You can also throw an error or pass it to the previous window if needed.
          newWindow.close();
        }
      });
    }
  };

  const fetchTimelines = (camera) => {
    if (!camera) {
      return;
    }
    axiosInstance
      .get(`${window.location.protocol}//${camera.host}/api/v1/record/${camera.sensorId}/timelines`)
      .then(async (response) => {
        enqueueSnackbar(`Success - got timelines for ${camera.name}`, {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        const timeline = response.data;
        console.log('timelines ', timeline);
        setTimelines(timeline);
        setDisabledIntervals(getTimelineGaps(timeline));
        if (timeline != null) {
          setSelectedInterval([new Date(timeline[0].startTime), new Date(timeline[timeline.length - 1].endTime)]);
        }
      })
      .catch((error) => {
        setTimelines(null);
        if (error.response.data == null)
          enqueueSnackbar(`Error - ${error.response.status}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        else
          enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
      });
  };

  const onChangeCallback = (selectedInterval) => {
    console.log('selectedInterval', selectedInterval);
    setSelectedInterval(selectedInterval);
  };

  const timelineScrubberErrorHandler = ({ error }) => {
    if (error) {
      console.log('Selected timeline with gap !');
    }
    setTimelineScrubberError(error);
  };

  const handleDownloadUsingTimeline = () => {
    enqueueSnackbar(`Pending - Download video files`, {
      variant: 'info',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });
    if (!camera) {
      enqueueSnackbar(`Error - Please select camera`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    console.log('selectedInterval: ', selectedInterval);
    if (selectedInterval == null || selectedInterval.length === 0) {
      enqueueSnackbar(`Error - Please select valid interval`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    const jsonData = {
      start_time: selectedInterval[0] ? selectedInterval[0].toISOString() : '',
      end_time: selectedInterval[1] ? selectedInterval[1].toISOString() : '',
    };
    downloadVideoDataApi(jsonData);
  };

  const handleDownloadUsingCalendar = () => {
    enqueueSnackbar(`Pending - Download video files`, {
      variant: 'info',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });
    if (!camera) {
      enqueueSnackbar(`Error - Please select camera`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    if (selectedInterval == null || selectedInterval.length === 0) {
      enqueueSnackbar(`Error - Please select valid interval`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    const jsonData = {
      start_time: calendarStartDate ? calendarStartDate.toISOString() : '',
      end_time: calendarEndDate ? calendarEndDate.toISOString() : '',
    };
    downloadVideoDataApi(jsonData);
  };

  const handleSetCamera = (cam) => {
    setCamera(cam);
    fetchTimelines(cam);
    console.log('cam: ', cam);
    if (!cam) {
      setTimelines(null);
      setSelectedInterval([]);
      setDisabledIntervals([]);
    }
  };

  const onOkStartTime = (value) => {
    if (value.length > 0 && value[0]) setCalendarStartDate(value[0].toDate());
    if (value.length > 0 && value[1]) setCalendarEndDate(value[1].toDate());
  };

  return (
    <>
      <Stack spacing={3} sx={{ mb: 2 }}>
        <SingleSelector
          id="download-videos"
          placeholder="Type to search"
          label="Select camera"
          value={camera}
          onChange={handleSetCamera}
          getOptionLabel={(option) => `${option.name}_${option.sensorId}`}
          options={cameraList}
        />
        {timelines != null && timelines.length > 0 && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Select using timeline
              </Typography>
            </Divider>
            <Box className="scrubber">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {`Selected range: ${
                  selectedInterval.length > 0
                    ? `${format(selectedInterval[0], 'HH:mm:ss.SSS')} to ${format(selectedInterval[1], 'HH:mm:ss.SSS')}`
                    : 'range not selected'
                }`}
              </Typography>

              <Box className="scrubber-container" sx={{ pt: 5, pb: 7, pl: 4, pr: 4 }}>
                <TimeRange
                  error={timelineScrubberError}
                  ticksNumber={3}
                  selectedInterval={selectedInterval}
                  timelineInterval={[
                    new Date(timelines[0].startTime),
                    new Date(timelines[timelines.length - 1].endTime),
                  ]}
                  onUpdateCallback={timelineScrubberErrorHandler}
                  onChangeCallback={onChangeCallback}
                  containerClassName="scrubber-container"
                  disabledIntervals={disabledIntervals}
                  step={1}
                  tooltipTag={'Time:'}
                  formatTick={(ms) => format(new Date(ms), 'MM/dd HH:mm:ss')}
                  formatTooltip={(ms) => format(new Date(ms), 'HH:mm:ss.SSS')}
                />
              </Box>
              <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
                <LoadingButton size="large" type="submit" variant="contained" onClick={handleDownloadUsingTimeline}>
                  Download
                </LoadingButton>
              </Box>
            </Box>
            <Box sx={{ zIndex: 999999, display: { xs: 'none', sm: 'none', md: 'inline' } }}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Select using calendar
                </Typography>
              </Divider>
              <RangePicker
                showTime={{ format: 'HH:mm:ss.SSS' }}
                format="YYYY-MM-DD HH:mm:ss.SSS"
                onOk={onOkStartTime}
              />
            </Box>
          </>
        )}
      </Stack>
      <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
        <LoadingButton size="large" type="submit" variant="contained" onClick={fetchCameraList} sx={{ mr: 1 }}>
          Refresh list
        </LoadingButton>
        <LoadingButton size="large" type="submit" variant="contained" onClick={handleDownloadUsingCalendar}>
          Download
        </LoadingButton>
      </Box>
    </>
  );
}
